import { useCallback, useEffect, useMemo, useState } from "react";
import { MonitorView } from "@/services/backend/qpm/monitors/types/monitor-view";
import { useQpmMonitorDetailsMutation } from "@/services/backend/qpm/monitors/api/monitor-api";
import { EditDetailsComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/edit-monitor-details";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { DetailsReq } from "@/services/backend/qpm/monitors/types/monitor-commands";
import { useDebouncedDirtyMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";

type DetailsDraft = Omit<DetailsReq, "id">;

function toDraft(
  details: MonitorView["details"] | MonitorSnapDTO["details"],
): DetailsDraft {
  return {
    description: details.description ?? "",
    installationType: details.installationType ?? "",
    heightMM: details.heightMM ?? 0,
    widthMM: details.widthMM ?? 0,
    depthMM: details.depthMM ?? 0,
    weightG: details.weightG ?? 0,
    coverageValue: details.coverageValue ?? 0,
    coveragePreText: details.coveragePreText ?? "",
    coveragePostText: details.coveragePostText ?? "",
    starterSpecification: details.starterSpecification ?? "",
    protectionLevel: details.protectionLevel ?? "",
  };
}

function detailsEqual(left: DetailsDraft, right: DetailsDraft): boolean {
  return (
    left.description === right.description &&
    left.installationType === right.installationType &&
    left.heightMM === right.heightMM &&
    left.widthMM === right.widthMM &&
    left.depthMM === right.depthMM &&
    left.weightG === right.weightG &&
    left.coverageValue === right.coverageValue &&
    left.coveragePreText === right.coveragePreText &&
    left.coveragePostText === right.coveragePostText &&
    left.starterSpecification === right.starterSpecification &&
    left.protectionLevel === right.protectionLevel
  );
}

export function MonitorDetailsComponent({
  monitorId,
  monitor,
  readOnly,
}: {
  monitorId: string;
  monitor: MonitorView | MonitorSnapDTO;
  readOnly?: boolean;
}) {
  const [update, saveState] = useQpmMonitorDetailsMutation();
  const externalDetails = useMemo(
    () => toDraft(monitor.details),
    [monitor.details],
  );
  const [syncedDetails, setSyncedDetails] = useState(externalDetails);
  const [draftDetails, setDraftDetails] = useState(externalDetails);

  useEffect(() => {
    setDraftDetails((current) =>
      detailsEqual(current, syncedDetails) ? externalDetails : current,
    );
    setSyncedDetails(externalDetails);
  }, [externalDetails, syncedDetails]);

  const request = useMemo<DetailsReq>(
    () => ({
      id: monitorId,
      ...draftDetails,
    }),
    [draftDetails, monitorId],
  );

  const persistDetails = useCallback(
    (nextRequest: DetailsReq) => update(nextRequest),
    [update],
  );

  useDebouncedDirtyMutationWithPersistenceStateContextUpdate(
    request,
    persistDetails,
    saveState.isLoading,
    saveState.error,
    saveState.isSuccess,
    saveState.reset,
    500,
    {
      toastError: true,
      skip: readOnly,
      baselineKey: monitorId,
      isDirty: (current, baseline) =>
        current.id !== baseline.id || !detailsEqual(current, baseline),
    },
  );

  const updateDraft = useCallback(
    (partial: Partial<DetailsDraft>) => {
      if (readOnly) {
        return;
      }
      setDraftDetails((current) => ({
        ...current,
        ...partial,
      }));
    },
    [readOnly],
  );

  return (
    <EditDetailsComponent
      details={draftDetails}
      type={monitor.type}
      readOnly={readOnly}
      onUpdate={updateDraft}
    />
  );
}
